import type { ExtendedObservation } from '@seen/core/models/observation'
import type { DefaultError } from '@tanstack/react-query'
import { useCallback } from 'react'
import { toast } from 'sonner'
import { useRemoveObservationRecord } from '#src/components/rich-text-editor/ObservationNode/useRemoveObservationRecord.ts'
import { useDeleteConfirmationStore } from '#src/hooks/useDeleteConfirmationStore.ts'

export interface ClearObservationAnswer {
  // One record, or every answer of a multi-select capture.
  observation?: ExtendedObservation
  observations?: ExtendedObservation[]
  label: string
  // Puts the node back to unanswered once the records are marked.
  onCleared: () => void
}

// Asks, then removes a node's recorded observation and hands the node back to
// its unanswered state.
export function useClearObservationAnswer(): (
  clear: ClearObservationAnswer
) => void {
  const removeRecord = useRemoveObservationRecord()
  const openConfirmation = useDeleteConfirmationStore((state) => state.open)
  return useCallback(
    ({
      observation,
      observations,
      label,
      onCleared,
    }: ClearObservationAnswer) => {
      const records = observations ?? (observation ? [observation] : [])
      if (records.length === 0) {
        return
      }
      openConfirmation({
        title: `Clear "${label}"?`,
        description:
          'The recorded value is removed from the chart and the field goes back to unanswered. It can still be recovered by contacting our team.',
        deleteText: 'Clear',
        onConfirm: () => {
          Promise.all(records.map((record) => removeRecord(record)))
            .then(() => {
              onCleared()
              toast.success(`${label} cleared`)
            })
            .catch((error: DefaultError) => {
              toast.error(`Failed to clear ${label}: ${error}`)
            })
        },
      })
    },
    [removeRecord, openConfirmation]
  )
}
